import { usePriceContext } from 'contexts';
import { usePricePartsFormatter } from 'hooks/format';
import type { PriceModel, ProductPrice } from 'models/price';
import { cn, cnm } from 'utils/classNames';
import { is } from 'utils/helpers';

import {
	getCurrency,
	getLabelAlign,
	getPriceValue,
	getTagTop,
	getTheme,
	isProductPrice,
	type RegularPriceVariant,
	type Size,
	useLabel,
	useScreenReaderText,
	useTagBottom,
} from './helpers';

interface Props {
	/** Additional container class names. */
	className?: string;

	/**
	 * Render as a regular price, even if it's something else like Campaign
	 * or JulaClub. This also hides any savings text.
	 */
	forceRegularPrice?: boolean;

	/** Custom label text. */
	label?: string;

	/** The main price. */
	price: PriceModel;

	regularPriceVariant?: RegularPriceVariant;

	/** The size of the price. */
	size?: Size;
}

/**
 * Price component with different themes and sizes.
 */
export default function Price({
	className,
	forceRegularPrice = false,
	label: labelProp,
	price: priceProp,
	regularPriceVariant = 'default',
	size = 'md',
}: Props) {
	const { usePriceExcVat } = usePriceContext();
	const formatPriceParts = usePricePartsFormatter();
	const price =
		forceRegularPrice && isProductPrice(priceProp)
			? ({ ...priceProp, priceType: 'Base' } satisfies ProductPrice)
			: priceProp;

	const theme = getTheme(price);
	const fallbackLabel = useLabel(price);
	const label = labelProp || fallbackLabel;
	const labelAlign = getLabelAlign(label);
	const tagTop = getTagTop(price, label);
	const tagBottom = useTagBottom(price);
	const priceParts = formatPriceParts(getPriceValue(price, usePriceExcVat));
	const currency = getCurrency(price, usePriceExcVat);
	const screenReaderText = useScreenReaderText({
		forceRegularPrice,
		label,
		price,
	});

	const isRotated = theme !== 'regular' && size !== 'xs';
	const hasTagBottom = tagBottom && size !== 'xs';
	const isRegularGraphic =
		theme === 'regular' && regularPriceVariant === 'graphic';

	const textSize = {
		12: 'text-[0.75rem]',
		14: 'text-[0.875rem]',
		24: 'text-[1.5rem]',
		32: 'text-[2rem]',
		40: 'text-[2.5rem]',
		60: 'text-[3.75rem]',
		72: 'text-[4.5rem]',
	} as const;

	const tagTopBottomBaseClasses = cn(
		'pointer-events-auto inline-block rounded-xs font-body leading-none whitespace-nowrap',
		is.oneOf(size, 'xs', 'sm') ? textSize[12] : textSize[14],
		is.oneOf(size, 'lg', 'xl') ? 'font-bold' : 'font-normal',

		// For tags with background
		theme !== 'regular' && [
			'forced-colors:border',
			is.oneOf(size, 'sm', 'xs') && 'px-1 py-0.75',
			size === 'md' && 'p-1',
			is.oneOf(size, 'lg', 'xl') && 'px-2 py-1.5',
		],
	);

	// Only using inline elements to keep HTML valid if a price is placed in
	// something like a button that doesn't allow block elements.
	// Due to that, add the paragraph role to distinguish it from 'non content
	// spans'. This is needed for target selection in FocusTarget.
	return (
		<span
			role="paragraph"
			className={cnm(
				'relative align-middle',
				tagTop || label || hasTagBottom
					? 'inline-flex flex-col items-start'
					: 'inline-block',
				// Disable pointer events to prevent the plate from ever stopping
				// text selection close to the price. They are restored on the
				// actual text parts.
				'pointer-events-none',
				isRotated && [
					'origin-top-left -rotate-[4deg]',
					// Rotation doesn't affect layout so compensate with some padding.
					size === 'sm' && 'pt-0.75',
					size === 'md' && 'pt-1.5',
					size === 'lg' && 'pt-2.5',
					size === 'xl' && 'pt-3',
				],
				className,
			)}
		>
			<span
				// Size and position the screen reader text to make the visual
				// screen reader cursor a bit better.
				className={cn(
					'sr-only left-0 text-sm',
					isRotated ? 'top-1/4' : 'top-2',
				)}
			>
				{screenReaderText}
			</span>

			{tagTop && (
				<span
					aria-hidden
					className={cn(
						tagTopBottomBaseClasses,
						theme === 'julaPro' && 'bg-pricetagJulapro text-pricetagJulapro',
						theme === 'julaClub' && 'bg-pricetagJulaclub text-pricetagJulaclub',
						is.oneOf(size, 'lg', 'xl') ? 'mb-1' : 'mb-0.5',
					)}
				>
					{tagTop}
				</span>
			)}

			<span
				aria-hidden
				className={cnm(
					'inline-block align-top font-heading font-bold whitespace-nowrap',
					// Restore pointer events disabled above.
					'pointer-events-auto',

					size === 'xs' && textSize[24],
					size === 'sm' && textSize[32],
					size === 'md' && textSize[40],
					size === 'lg' && textSize[60],
					size === 'xl' && textSize[72],
					// Leading after text size classes, or it gets merged away.
					// https://github.com/dcastil/tailwind-merge/issues/573
					'leading-none',

					theme === 'regular' && 'text-base',
					// Backplate
					(theme !== 'regular' || isRegularGraphic) && [
						'forced-colors:border',

						isRegularGraphic && 'bg-brandDecorative text-negative',
						theme === 'campaign' && 'bg-pricetagCampaign text-pricetagCampaign',
						theme === 'julaClub' && 'bg-pricetagJulaclub text-pricetagJulaclub',
						theme === 'julaPro' && 'bg-pricetagJulapro text-pricetagJulapro',

						// Padding
						size === 'xs' && ['p-1.5', label && 'py-1'],
						is.oneOf(size, 'sm', 'md') && [
							'p-2',
							label && 'pb-1.5',
							tagBottom && 'pb-2.5',
						],
						is.oneOf(size, 'lg', 'xl') && [
							'p-3',
							label && !tagBottom && 'pb-2.5',
						],

						// Radius
						is.oneOf(size, 'lg', 'xl') ? 'rounded-lg' : 'rounded-md',
					],
				)}
			>
				{label && (
					<span
						className={cn(
							'block leading-none whitespace-nowrap',
							// Place above the main text to handle text backplate in
							// forced colors mode.
							'relative z-1',

							// Text style
							labelAlign === 'right' && 'text-right',
							is.oneOf(size, 'md', 'lg', 'xl') ? 'font-bold' : 'font-normal',
							is.oneOf(size, 'lg', 'xl') ? 'font-heading' : 'font-body',

							// Text size and spacing
							size === 'xs' && [textSize[12], '-mb-px'],
							is.oneOf(size, 'sm', 'md') && [textSize[14], '-mt-0.5 -mb-0.75'],
							is.oneOf(size, 'lg', 'xl') && [textSize[24], '-mt-0.75 -mb-1.5'],
						)}
					>
						{label}
					</span>
				)}

				{priceParts.main}
				{priceParts.decimal && (
					<sup className="top-0 align-super text-[50%] leading-none">
						{priceParts.decimal}
					</sup>
				)}
				{currency && (
					<span
						className={cn(
							'pr-[0.08em] -tracking-[0.125em]',
							size !== 'xs' && '-ml-[0.02em]',
						)}
					>
						{currency}
					</span>
				)}
			</span>

			{hasTagBottom && (
				<span
					aria-hidden
					className={cn(
						tagTopBottomBaseClasses,
						'relative z-1 self-end',
						theme === 'regular' && [
							'-mr-0.5',
							is.oneOf(size, 'lg', 'xl') ? '-mt-1.5' : '-mt-0.5',
						],
						theme !== 'regular' && [
							'bg-saving text-pricetagSaving',
							'-mr-2 ml-2',
							size === 'sm' && '-mt-2.5',
							size === 'md' && '-mt-3',
							is.oneOf(size, 'lg', 'xl') && '-mt-3.5',
						],
					)}
				>
					{tagBottom}
				</span>
			)}
		</span>
	);
}
Price.displayName = 'Price';
